import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  getLiveProductLocations,
  getOrdersOverview,
  type CountDiscrepancy,
  type OverviewReceiving,
} from '@/api/orders';
import { PrintableCountSheet } from '@/features/orders/PrintableCountSheet';

type Row = CountDiscrepancy & { rcv: OverviewReceiving };

/**
 * A count sheet for ONE receiving, at its own URL, so the warehouse can be
 * handed a link instead of someone re-finding the row in Decide and printing
 * from there.
 *
 * The row filter is the same as the Decide view's print button, and for the
 * same reasons: `recount` always prints (the instruction was already given),
 * `settle` prints only while the receiving is unbalanced (once the money is
 * closed no count changes it), and `recapture` never prints (that verdict says
 * the INVOICE was misread — the shelf is not in question).
 */
export function PrintCountSheetPage() {
  const { receipt } = useParams<{ receipt: string }>();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [locations, setLocations] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!receipt) return;
    let cancelled = false;
    (async () => {
      try {
        const overview = await getOrdersOverview();
        const rcv = overview.receivings.find((r) => r.receiptId === receipt);
        if (!rcv) {
          if (!cancelled) setError(`No mirrored receiving ${receipt}.`);
          return;
        }
        const snapshot: Row[] = (rcv.discrepancies ?? [])
          .filter((d) => d.action === 'recount' || (d.action === 'settle' && !rcv.balancedAt))
          .map((d) => ({ ...d, rcv }));
        if (snapshot.length === 0) {
          if (!cancelled) {
            setError('Nothing for the warehouse to count on this receiving.');
          }
          return;
        }
        const pids = [...new Set(snapshot.map((r) => r.pid).filter((p): p is number => p != null))];
        const locs = pids.length ? await getLiveProductLocations(pids) : {};
        if (cancelled) return;
        setLocations(locs);
        setRows(snapshot);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build the sheet');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [receipt]);

  if (error) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-danger-ink">{error}</p>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => navigate('/work/discrepancies')}
        >
          Back to Decide
        </button>
      </div>
    );
  }

  if (!rows) {
    return (
      <div className="flex min-h-svh items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Building the count sheet…
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-svh flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          The print dialog should be open. Nothing renders on screen — the sheet is
          print-only.
        </p>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => window.print()}
        >
          Print again
        </button>
      </div>
      {/* Portals itself under <body>, outside #root, and prints alone. */}
      <PrintableCountSheet
        rows={rows}
        locations={locations}
        onDone={() => navigate('/work/discrepancies')}
      />
    </>
  );
}
